import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { doc, getDoc } from "firebase/firestore";
import { logoutUser, getUserProfile, db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { MdPerson } from "react-icons/md";

const ADMIN_GUIDE_URL = "https://docs.google.com/document/d/1yMrl-4JsozGHP0YCynEddZjqqf6FAPS6J3LM6z5zNpo";
const STUDENT_GUIDE_URL = "https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40";

const UserIcon = () => {
    const { user, groupMembers, courseInfo, groupInfo, isAdmin, groupName } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const [loading, setLoading] = useState(true);
    const [isProjectManager, setIsProjectManager] = useState(false);
    const popupRef = useRef(null);
    const buttonRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const hasGroupMembers = useMemo(() => groupMembers && groupMembers.length > 0, [groupMembers]);
    const isClassSelectionPage = location.pathname === "/student/home" || location.pathname.startsWith("/admin");

    const isOnAdminCourseSubpage =
        location.pathname === "/admin/home/group_viewer" ||
        location.pathname === "/admin/home/group_manager";

    const effectiveCourseId = isOnAdminCourseSubpage
        ? location.state?.course?.id
        : courseInfo?.courseId;

    const isAdminInCourse =
        location.pathname !== "/settings" &&
        !location.pathname.startsWith("/admin/course-settings") &&
        (
            (location.pathname.startsWith("/admin") &&
                !location.pathname.startsWith("/admin/home") &&
                !!courseInfo?.courseId) ||
            (isOnAdminCourseSubpage && !!location.state?.course?.id)
        );



    useEffect(() => {
        const fetchUserName = async () => {
            if (user && user.email) {
                try {
                    const profile = await getUserProfile(user.email);
                    if (profile && profile.name) {
                        setUserName(profile.name);
                    } else if (user.displayName) {
                        setUserName(user.displayName);
                    } else {
                        const emailPrefix = user.email.split('@')[0];
                        setUserName(emailPrefix);
                    }
                } catch (error) {
                    console.error('Error fetching user profile:', error);
                    if (user.displayName) {
                        setUserName(user.displayName);
                    } else {
                        const emailPrefix = user.email.split('@')[0];
                        setUserName(emailPrefix);
                    }
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchUserName();
    }, [user]);

    useEffect(() => {
        const fetchProjectManagerAccess = async () => {
            if (!user?.email) {
                setIsProjectManager(false);
                return;
            }

            try {
                const snap = await getDoc(doc(db, "Users", user.email));
                setIsProjectManager(snap.exists() && snap.data()?.isProjectManager === true);
            } catch (error) {
                console.error("Error checking project manager access:", error);
                setIsProjectManager(false);
            }
        };

        fetchProjectManagerAccess();
    }, [user?.email]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const togglePopup = () => {
        setIsOpen(!isOpen);
    };

    const handleSettings = () => {
        const hasCourseContext = Boolean(
            courseInfo?.courseId ||
            location.state?.course?.id ||
            (groupInfo && typeof groupInfo === "object" && groupInfo?.name)
        );
        const settingsCourseName =
            location.state?.course?.name ||
            groupInfo?.name ||
            courseInfo?.name ||
            "";
        const settingsGroupName =
            groupName ||
            location.state?.groupName ||
            "";

        const settingsHeaderMode =
            location.pathname === "/student/home" ||
            location.pathname === "/admin/home" ||
            !hasCourseContext
                ? "home"
                : "course";
        const settingsHeaderVariant =
            settingsHeaderMode === "course"
                ? (isAdmin ? "admin-course" : "student-course")
                : "home";

        navigate('/settings', {
            state: {
                settingsHeaderMode,
                settingsHeaderVariant,
                settingsCourseName,
                settingsGroupName,
            },
        });
        setIsOpen(false);
    };

    const handleLogout = () => {
        console.log("User logged out");
        logoutUser();
        setIsOpen(false);
    };

    const handleCourseSettings = () => {
        navigate(`/admin/course-settings/${effectiveCourseId}`);
        setIsOpen(false);
    };

    const handleMaintenanceNotes = () => {
        navigate("/maintenance/release-notes");
        setIsOpen(false);
    };

    const handleMaintenanceBanner = () => {
        navigate("/maintenance/banner");
        setIsOpen(false);
    };

    const handleMaintenanceIssues = () => {
        navigate("/maintenance/issues");
        setIsOpen(false);
    };

    const handleGuide = () => {
        const guideUrl = location.pathname.startsWith("/admin")
            ? ADMIN_GUIDE_URL
            : STUDENT_GUIDE_URL;
        window.open(guideUrl, "_blank", "noopener,noreferrer");
        setIsOpen(false);
    };

    if (!user) return null;

    return (
        <UserIconWrapper>
            <IconButton ref={buttonRef} onClick={togglePopup} aria-haspopup="menu" aria-expanded={isOpen}>
                <StyledProfileIcon />
            </IconButton>
            {isOpen && (
                <Popup ref={popupRef}>
                    <UserName>{user.displayName}</UserName>
                    <MenuButton onClick={handleSettings}>Settings</MenuButton>
                    <MenuButton onClick={handleGuide}>
                        {location.pathname.startsWith("/admin") ? "Admin Guide" : "User Guide"}
                    </MenuButton>
                    {isAdminInCourse && (
                        <MenuButton onClick={handleCourseSettings}>
                            Course Settings
                        </MenuButton>
                    )}
                    {isProjectManager && (
                        <MenuButton onClick={handleMaintenanceNotes}>
                            Update Notes
                        </MenuButton>
                    )}
                    {isProjectManager && (
                        <MenuButton onClick={handleMaintenanceBanner}>
                            Maintenance Banner
                        </MenuButton>
                    )}
                    {isProjectManager && (
                        <MenuButton onClick={handleMaintenanceIssues}>
                            Issue Reports
                        </MenuButton>
                    )}
                    {hasGroupMembers && !isClassSelectionPage && (
                        <MenuButton
                            onClick={() => {
                                navigate("/connections");
                            }}
                            disabled={
                                location.pathname.startsWith("/admin") ||
                                location.pathname === "/student/home"
                            }
                        >
                            Connections
                        </MenuButton>
                    )}
                    <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
                </Popup>
            )}
        </UserIconWrapper>
    );
};

const UserIconWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const IconButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 8px;
  background: #9333ea;
  color: #fff;           /* react-icons uses currentColor */
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s, color 0.2s;

  &:hover {
    transform: scale(1.05);
    background-color: #7e22ce;
    color: #e5e7eb;      /* fixed: removed trailing '#' */
  }
  &:active { transform: scale(0.95); }
`;



const StyledProfileIcon = styled(MdPerson)`
  width: 100%;
  height: 100%;
`;

const Popup = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    background-color: white;
    padding: 12px;
    border-radius: 8px;
    flex-direction: column;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    min-width: 150px;
    white-space: nowrap;
    width: max-content;        
    animation: fadeIn 0.2s ease-out;
    display: flex;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const UserName = styled.h2`
    font-size: 14px;
    padding: 8px 0 12px 0;
    margin: 0;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MenuButton = styled.button`
    background-color: transparent;
    color: #333;
    border: none;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    text-align: center;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    width: 100%;
    margin-top: 8px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f5f5f5;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const LogoutButton = styled.button`
    background-color: #9333ea;
    color: white;
    border: none;
    padding: 10px 15px;
    border-radius: 5px;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    width: 100%;
    margin-top: 8px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #7e22ce;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const ConnectRepoButton = styled.button`
  width: 100%;
  background-color: #f3f4f6; /* Tailwind's bg-gray-100 */
  color: #4b5563;            /* Tailwind's text-gray-600 */
  padding: 0.75rem 1rem;     /* py-3 px-4 */
  border-radius: 0.5rem;     /* rounded-lg */
  font-size: 14px;       /* text-lg */
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e5e7eb; /* Tailwind's bg-gray-200 */
  }
`;

export default UserIcon;
